import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AuthLayout } from './AuthLayout';
import styles from './AuthLayout.module.css';
import { PROFILES, profileByValue, profileFromSlug } from './profiles';
import profileStyles from './RegisterPage.module.css';

import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { Field } from '@/components/Field';
import { ApiError } from '@/lib/apiClient';
import { authApi } from '@/lib/authApi';

/**
 * Tela de cadastro (RF-01.01).
 *
 * <p>
 * O cadastro é B2B: quem entra é o fundador de uma <b>empresa</b>, com um perfil
 * (comprador, fornecedor ou ambos) escolhido na landing e mantido em
 * <code>?perfil=</code> — visível e trocável aqui, para o cadastro nunca ser
 * ambíguo sobre o que a pessoa está criando.
 * </p>
 * <p>
 * A confirmação é <b>sempre a mesma</b>, exista a conta ou não. É o outro lado
 * da anti-enumeração do servidor: se a API responde igual nos dois casos e a
 * interface mostrasse "e-mail já cadastrado", o esforço do backend seria
 * desfeito pela tela (docs/10 §2).
 * </p>
 */
export function RegisterPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const profile = profileFromSlug(searchParams.get('perfil')) ?? profileByValue('Buyer');

  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Readonly<Record<string, readonly string[]>>>({});
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState<{ readonly verificationRequired: boolean } | null>(null);

  function chooseProfile(slug: string): void {
    // replace: trocar o perfil não deve empilhar histórico, senão o "voltar" do
    // navegador percorreria cada clique.
    setSearchParams({ perfil: slug }, { replace: true });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setIsSubmitting(true);

    try {
      const result = await authApi.register({
        name,
        companyName,
        cnpj,
        email,
        password,
        profile: profile.value,
      });

      setDone({ verificationRequired: result.emailVerificationRequired });
    } catch (caught) {
      if (caught instanceof ApiError && caught.isValidation) {
        setFieldErrors({
          name: caught.fieldErrors('name'),
          companyName: caught.fieldErrors('companyName'),
          cnpj: caught.fieldErrors('cnpj'),
          email: caught.fieldErrors('email'),
          password: caught.fieldErrors('password'),
        });
      } else {
        setError('Não foi possível criar a conta agora. Tente novamente em instantes.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (done !== null) {
    return (
      <AuthLayout
        footer={<Link to="/entrar">Ir para o acesso</Link>}
        title={done.verificationRequired ? 'Confira seu e-mail' : 'Conta criada'}
      >
        {done.verificationRequired ? (
          <Alert tone="success" title="Cadastro recebido">
            Se este endereço puder ser usado, enviamos um link de confirmação para{' '}
            <strong>{email}</strong>. O link vale por 24 horas.
          </Alert>
        ) : (
          <Alert tone="success" title="Tudo pronto">
            A conta de <strong>{companyName}</strong> foi criada. Você já pode{' '}
            <Link to="/entrar">entrar</Link> e começar.
          </Alert>
        )}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      footer={
        <>
          Já tem conta? <Link to="/entrar">Entrar</Link>
        </>
      }
      subtitle="Cadastre sua empresa em menos de um minuto."
      title="Criar conta"
    >
      <form className={styles.form} noValidate onSubmit={(event) => void handleSubmit(event)}>
        {error !== null && <Alert tone="error">{error}</Alert>}

        <fieldset className={profileStyles.profile}>
          <legend className={profileStyles.legend}>Sua empresa atua como</legend>

          <div className={profileStyles.options}>
            {PROFILES.map((option) => {
              const active = option.value === profile.value;

              return (
                <button
                  aria-pressed={active}
                  className={`${profileStyles.option} ${active ? profileStyles.optionActive : ''}`}
                  key={option.value}
                  onClick={() => chooseProfile(option.slug)}
                  type="button"
                >
                  {option.label}
                </button>
              );
            })}
          </div>

          <p className={profileStyles.tagline}>{profile.tagline}</p>
        </fieldset>

        <Field
          autoComplete="name"
          errors={fieldErrors.name}
          label="Seu nome"
          name="name"
          onChange={(event) => setName(event.target.value)}
          required
          value={name}
        />

        <Field
          autoComplete="organization"
          errors={fieldErrors.companyName}
          label="Nome da empresa"
          name="companyName"
          onChange={(event) => setCompanyName(event.target.value)}
          required
          value={companyName}
        />

        <Field
          errors={fieldErrors.cnpj}
          inputMode="numeric"
          label="CNPJ"
          name="cnpj"
          onChange={(event) => setCnpj(formatCnpj(event.target.value))}
          placeholder="00.000.000/0000-00"
          required
          value={cnpj}
        />

        <Field
          autoComplete="email"
          errors={fieldErrors.email}
          inputMode="email"
          label="E-mail"
          name="email"
          onChange={(event) => setEmail(event.target.value)}
          required
          type="email"
          value={email}
        />

        <Field
          autoComplete="new-password"
          errors={fieldErrors.password}
          hint="Mínimo de 10 caracteres. Uma frase que você lembre vale mais que símbolos."
          label="Senha"
          name="password"
          onChange={(event) => setPassword(event.target.value)}
          required
          type="password"
          value={password}
        />

        <Button loading={isSubmitting} type="submit">
          Criar conta
        </Button>
      </form>
    </AuthLayout>
  );
}

/**
 * Formata o CNPJ enquanto se digita. O servidor remove a máscara — isto é só
 * conforto visual e uma pista de quantos dígitos faltam.
 */
function formatCnpj(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);

  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}
